import * as React from 'react';
import { useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import {
    Box,
    ClickAwayListener,
    Grow,
    MenuList,
    Paper,
    Popper,
    PopperProps,
    Typography,
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { Industrial_Sensors_Products_ID } from '../layout/Header/Product';
import { useDim } from '../../contexts/DimContext';
import { useResponsive } from '../../contexts/ResponsiveContext';
import Image from 'next/image';

export type MenuItemData = {
    id?: string;
    englishTitle: string;
    title: string;
    level: number;
    route?: string;
    isHeader?: boolean; // If true, show an "overview" header link at the top of submenus
    children?: Array<MenuItemData>;
};

export function isMenuItemData(item: any): item is MenuItemData {
    return item && typeof item.title === 'string' && typeof item.level === 'number';
}

export function isArrayOfMenuItemData(items: any[]): items is MenuItemData[] {
    return items.every(isMenuItemData);
}

type SubMenuProps<T extends MenuItemData> = {
    isMenuOpen: boolean;
    anchorEl: (EventTarget & Element) | null | HTMLElement;
    menuLevels: number;
    menuItemsData: T[];
    onClose: (menuItem?: T) => void;
    mainMenuStartOffset: number;
    mainMenuExpandOffset: number;
    // This is the index of the main menu item that this submenu belongs to; used for calculating
    // horizontal offsets when submenu childs are opened (the submenu shifts to the left).
    mainMenuIndex: number;
};

const duration = 150;

const animateOffset = (
    startOffset: number,
    targetOffset: number,
    duration: number,
    setModifiers: React.Dispatch<React.SetStateAction<PopperProps['modifiers']>>
) => {
    let start: number | null = null;

    const step = (timestamp: number) => {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        const currentOffset = startOffset + (targetOffset - startOffset) * progress;
        setModifiers([
            {
                name: 'offset',
                options: {
                    offset: [currentOffset, 0],
                },
            },
        ]);

        if (progress < 1) {
            requestAnimationFrame(step);
        }
    };

    requestAnimationFrame(step);
};

const menuItemTitleStyle: React.CSSProperties = {
    backgroundColor: '#f8f8f8',
    fontSize: '1.25rem',
    fontWeight: '400',
    fontFamily: 'Poppins',
    lineHeight: '2rem',
    color: '#B75332',
    border: 'none',
    height: '4rem',
    position: 'relative', // Add relative positioning to parent element
    paddingLeft: '2rem',
};

const menuItemTitleAfterStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    bottom: '0.2rem',
    left: '1.5rem',
    right: '1rem',
    borderBottom: '1px solid gray',
};

function SubMenu<T extends MenuItemData>({
    isMenuOpen,
    anchorEl,
    menuItemsData,
    menuLevels,
    onClose,
    mainMenuStartOffset,
    mainMenuExpandOffset,
}: SubMenuProps<T>) {
    const { acquire, release } = useDim();

    const { isMaxScreen, isScreenXxl, isScreenXl } = useResponsive();

    const [anchors, setAnchors] = React.useState<{
        elements: Array<null | HTMLElement>;
        menuItems: Array<null | typeof menuItemsData>;
    }>({
        elements: new Array(menuLevels).fill(null),
        menuItems: new Array(menuLevels).fill(null),
    });
    const [mainMenuModifiers, setMainMenuModifiers] = React.useState<PopperProps['modifiers']>([]);
    const [parentMenuItem, setParentMenuItem] = React.useState<T | null>(null);
    const [openSubMenu, setOpenSubMenu] = React.useState<boolean>(true);
    const navigate = useNavigate();
    const handleOpen = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        level = 0,
        nestedMenuItems: T[] = menuItemsData
    ) => {
        const target = event.currentTarget as HTMLElement;

        const currentMainMenuOffset =
            mainMenuModifiers && mainMenuModifiers.length > 0
                ? mainMenuModifiers[0]?.options?.offset?.[0] ?? null
                : null;

        if (level === 1) {
            if (currentMainMenuOffset && currentMainMenuOffset !== mainMenuExpandOffset) {
                animateOffset(
                    mainMenuStartOffset,
                    mainMenuExpandOffset,
                    duration,
                    setMainMenuModifiers
                );
            }
        }

        setAnchors((prevAnchors) => ({
            elements: prevAnchors.elements.map((element, index) =>
                index === level ? target : element
            ),
            menuItems: prevAnchors.menuItems.map((element, index) =>
                index === level ? nestedMenuItems : element
            ),
        }));
    };

    const handleClose = (menuItemData?: T) => {
        onClose(menuItemData);
        setAnchors({
            elements: new Array(menuLevels).fill(null),
            menuItems: new Array(menuLevels).fill(null),
        });
        setOpenSubMenu(false);
        setParentMenuItem(null);
    };

    React.useEffect(() => {
        if (!parentMenuItem) {
            animateOffset(
                mainMenuExpandOffset,
                mainMenuStartOffset,
                duration,
                setMainMenuModifiers
            );
        }
    }, [parentMenuItem, mainMenuExpandOffset, mainMenuStartOffset]);

    const openItem = (menuItem: T) => {
        const route = menuItem.route;
        if (!route) {
            console.error('Menu item does not have a route defined');
            return;
        }
        if (route.startsWith('http')) {
            window.open(route, '_blank');
            return;
        }

        navigate(route, { state: { shouldClearAllProps: true } });
        handleClose(menuItem);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const itemWithoutSubMenu = anchors.elements.every(
            (element) => !event.composedPath().includes(element!)
        );

        if (itemWithoutSubMenu) {
            handleClose();
        }
    };

    const handleClickMenuItem = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        menuItem: T
    ) => {
        if (parentMenuItem?.title === menuItem.title) {
            setOpenSubMenu(false);
            setMainMenuModifiers([]);
            setParentMenuItem(null);
            return;
        }
        if (menuItem.children && menuItem.children.length > 0) {
            if (isArrayOfMenuItemData(menuItem.children)) {
                setOpenSubMenu(true);
                setParentMenuItem(menuItem);
                handleOpen(event, menuItem.level + 1, menuItem.children as T[]);
            } else {
                // Handle case where children are not of type T[]
                console.error('Children are not of expected type');
            }
            return;
        }

        openItem(menuItem);
    };

    /**
     * Invokes handleClose() with the parent menuItemData.
     */
    const handleClickSubmenuHeader = () => {
        handleClose(parentMenuItem || undefined);
    };

    const maskUrl = '/externalLink.svg';

    function getPaper(index: number) {
        return (
            <MenuList
                id={`nested-menu-${index}`}
                aria-labelledby="nested-button"
                sx={{
                    backgroundColor: '#f8f8f8',
                    paddingTop: 0,
                    marginLeft: '-1rem',
                    minWidth: 360,
                    boxShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
                }}
            >
                <MenuItem
                    sx={menuItemTitleStyle}
                    onClick={() => {
                        handleClickSubmenuHeader();
                    }}
                >
                    {parentMenuItem?.title}
                    <span style={menuItemTitleAfterStyle}></span>
                </MenuItem>
                {(anchors.menuItems[index] ?? []).map((item, _itemIndex) => (
                    <MenuItem
                        key={item.title}
                        aria-haspopup={!!item.children}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#B75332',
                                textDecoration: 'underline',
                                textDecorationColor: '#B75332',
                            },
                        }}
                        aria-expanded={
                            item.children
                                ? anchors.elements.some(
                                      (element) => element?.innerText === item.title
                                  )
                                : undefined
                        }
                        onClick={(event) => {
                            handleClickMenuItem(event, item);
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                alignItems: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <Typography
                                sx={{
                                    gap: '1.25rem',
                                    paddingLeft: '1rem',
                                    lineHeight: '118.8%',
                                    fontFamily: 'Poppins',
                                    fontWeight: '400',
                                    marginTop: '0.1rem',
                                }}
                            >
                                {item.title}
                                {item.id &&
                                    parseInt(item.id) === Industrial_Sensors_Products_ID && (
                                        <Image
                                            src={maskUrl}
                                            alt="external link"
                                            width={16}
                                            height={16}
                                            style={{
                                                marginLeft: '0.5rem',
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                    )}
                            </Typography>
                            {item.children && item.children.length > 0 && (
                                <ChevronRight fontSize="small" />
                            )}
                        </Box>
                    </MenuItem>
                ))}
            </MenuList>
        );
    }

    // 只要主選單開啟時就把畫面 dim；關閉時復原
    useEffect(() => {
        if (!isMenuOpen) return;
        acquire(); // turn ON (increments counter)
        return () => release(); // paired release when submenu closes/unmounts
    }, [isMenuOpen, acquire, release]);
    return (
        <>
            {isMenuOpen && (
                <>
                    <Popper
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        placement="bottom-start"
                        transition
                        modifiers={mainMenuModifiers}
                        // 參照header.css 把這個設為9999
                        style={{
                            zIndex: 9999,
                            borderTop:
                                isMaxScreen || isScreenXxl || isScreenXl
                                    ? '1px solid #E5673E'
                                    : 'none',
                        }}
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: anchors.elements[1] ? 'top right' : 'top left',
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: '1rem' }}>
                                    <Paper sx={{ borderRadius: '0' }}>
                                        <ClickAwayListener
                                            onClickAway={handleClickAway}
                                            touchEvent={false}
                                        >
                                            <MenuList
                                                autoFocusItem={Boolean(anchorEl)}
                                                id="nested-menu"
                                                aria-labelledby="nested-button"
                                                sx={{
                                                    paddingTop: 0,
                                                    paddingBottom: '.5rem',
                                                    minWidth: 304,
                                                }}
                                            >
                                                {(anchors.menuItems[0] ?? menuItemsData).map(
                                                    (item) => {
                                                        const isActiveParent =
                                                            parentMenuItem?.id === item.id;
                                                        return (
                                                            <MenuItem
                                                                data-title={item.title}
                                                                selected={isActiveParent}
                                                                sx={{
                                                                    '&:hover': {
                                                                        color: '#B75332',
                                                                    },
                                                                    color:
                                                                        item.isHeader ||
                                                                        isActiveParent
                                                                            ? '#B75332'
                                                                            : 'black',
                                                                }}
                                                                key={item.title}
                                                                aria-haspopup={!!item.children}
                                                                aria-expanded={
                                                                    item.children
                                                                        ? anchors.elements.some(
                                                                              (element) =>
                                                                                  element?.innerText ===
                                                                                  item.title
                                                                          )
                                                                        : undefined
                                                                }
                                                                onClick={(event) => {
                                                                    handleClickMenuItem(
                                                                        event,
                                                                        item
                                                                    );
                                                                }}
                                                            >
                                                                <Box
                                                                    data-testid={`${item.title}-nav`}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'flex-start',
                                                                        width: '100%',
                                                                        alignItems: 'center',
                                                                    }}
                                                                    component={'a'}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            gap: '1.25rem',
                                                                            paddingLeft: '0.8rem',
                                                                            width: '100%',
                                                                            lineHeight: '118.8%',
                                                                            fontFamily: 'Poppins',
                                                                            paddingRight: '0.2rem',
                                                                            fontWeight: '400',
                                                                            '&:hover + .chevron-right':
                                                                                {
                                                                                    transform:
                                                                                        'translateX(0.4rem)',
                                                                                },
                                                                        }}
                                                                    >
                                                                        {item.title}
                                                                    </Typography>
                                                                    {item.children &&
                                                                        item.children.length >
                                                                            0 && (
                                                                            <ChevronRight
                                                                                fontSize="small"
                                                                                className="chevron-right"
                                                                                sx={{
                                                                                    transition:
                                                                                        'transform 0.3s ease',
                                                                                }}
                                                                            />
                                                                        )}
                                                                </Box>
                                                            </MenuItem>
                                                        );
                                                    }
                                                )}
                                            </MenuList>
                                        </ClickAwayListener>
                                    </Paper>
                                    {openSubMenu &&
                                        anchors.elements.map(
                                            (anchorElement, index) =>
                                                anchorElement && (
                                                    <React.Fragment key={index}>
                                                        {getPaper(index)}
                                                    </React.Fragment>
                                                )
                                        )}
                                </Box>
                            </Grow>
                        )}
                    </Popper>
                    {/*要改變submenu的位置是在這邊*/}
                </>
            )}
        </>
    );
}

export default SubMenu;
